import { memo, useCallback, useEffect, useState } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import ReorderWrapper from '@/components/atoms/ReorderWrapper/ReorderWrapper';
import ToolkitAccordionWrapper from '@/components/organisms/Toolkit/ToolkitAccordionWrapper';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useDebouncedCallback from '@/hooks/useDebouncedCallback';
import { removeRequisiteOption, updateRequisiteOption } from '@/redux/nodes/actions';
import { RequisiteOptionType } from '@/shared/types/node';
import { createYupResolver } from '@/util/forms/createYupResolver';
import { capitalizeFirstLetter } from '@/util/util';
import { requisiteOptionSchema } from '@/util/validation/schemas/dialog-schemas';

type Props = {
  index: number;
  expanded: boolean;
  option: RequisiteOptionType;
  requisiteId: string;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  dropItem: () => void;
  updateErrors: (key: string, error: string | null) => void;
};

const RequisiteOption = ({
  index,
  expanded,
  option,
  requisiteId,
  moveItem,
  dropItem,
  updateErrors,
}: Props) => {
  const getCardIndex = useCallback((index: number) => index, []);
  const [expandedState, setExpandedState] = useState(expanded);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formMethods = useForm<RequisiteOptionType>({
    mode: 'onChange',
    defaultValues: { ...option },
    resolver: createYupResolver<RequisiteOptionType>(requisiteOptionSchema),
  });
  const {
    formState: { errors },
    getValues,
    control,
    trigger,
  } = formMethods;

  const labelErrorMessage = capitalizeFirstLetter(errors?.label?.message);
  const textErrorMessage = capitalizeFirstLetter(errors?.text?.message);

  const debouncedUpdateOption = useDebouncedCallback(
    () => {
      dispatch(
        updateRequisiteOption({ requisiteId, optionId: option.option_id, option: getValues() })
      );
    },
    200,
    [dispatch, requisiteId, option.option_id]
  );

  const clearOptionDialogErrors = useCallback(() => {
    if (labelErrorMessage) {
      updateErrors(`label-${option.option_id}`, undefined);
    }
    if (textErrorMessage) {
      updateErrors(`text-${option.option_id}`, undefined);
    }
  }, [labelErrorMessage, option.option_id, textErrorMessage, updateErrors]);

  const removeOption = useCallback(() => {
    clearOptionDialogErrors();
    debouncedUpdateOption.cancel();
    dispatch(removeRequisiteOption({ requisiteId, optionId: option.option_id }));
  }, [dispatch, option.option_id, clearOptionDialogErrors, requisiteId, debouncedUpdateOption]);

  const handleCollapse = useCallback(() => {
    setExpandedState((expanded) => !expanded);
  }, []);

  // Dialog errors
  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    updateErrors(`label-${option.option_id}`, labelErrorMessage);
  }, [labelErrorMessage, option.option_id, updateErrors]);

  useEffect(() => {
    updateErrors(`text-${option.option_id}`, textErrorMessage);
  }, [option.option_id, textErrorMessage, updateErrors]);

  // If there are errors, expand the accordion
  useEffect(() => {
    if (!isEmpty(errors)) {
      setExpandedState(true);
    }
  }, [errors]);

  return (
    <div className="mb-2">
      <ReorderWrapper
        dragId="requisite-option"
        index={index}
        placeholderHeight={36}
        moveItem={moveItem}
        dropItem={dropItem}
        getIndex={() => getCardIndex(index)}
        canDrag={!expandedState}
      >
        <ToolkitAccordionWrapper
          onChange={handleCollapse}
          expanded={expandedState}
          title={option.label}
          handleDelete={removeOption}
        >
          <Form {...formMethods}>
            <FormField
              control={control}
              name="label"
              render={({ field }) => (
                <FormItem className="mb-6">
                  <FormLabel>{t('common.label')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={t('dialog.carousel.button')}
                      onChange={(event) => {
                        field.onChange(event);
                        debouncedUpdateOption();
                      }}
                    />
                  </FormControl>
                  <FormMessage>{labelErrorMessage}</FormMessage>
                </FormItem>
              )}
            />
            <div>
              <FormField
                control={control}
                name="text"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.text')}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('dialog.carousel.button')}
                        onChange={(event) => {
                          field.onChange(event);
                          debouncedUpdateOption();
                        }}
                      />
                    </FormControl>
                    <FormMessage>{textErrorMessage}</FormMessage>
                  </FormItem>
                )}
              />
            </div>
          </Form>
        </ToolkitAccordionWrapper>
      </ReorderWrapper>
    </div>
  );
};

export default memo(RequisiteOption);
